import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatError, isValidEmail } from "@/lib/api";
import { toast } from "@/lib/toast";
import { ArrowRight, CheckCircle2, Building2 } from "lucide-react";

// Public Business & Dealer waitlist — pure lead capture. Lands in Admin → Business Applications →
// Waitlist tab, plus a confirmation email to the submitter. No pricing / seats / tiers / verification.
export default function BusinessWaitlist() {
  const [params] = useSearchParams();
  const source = params.get("from") || "direct";
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", business_name: "", location: "", website: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSubmit =
    form.name.trim().length >= 2 && isValidEmail(form.email) &&
    form.business_name.trim().length >= 1 && form.location.trim().length >= 2 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error("Please add your name, a valid email, business name, and location."); return; }
    setBusy(true);
    try {
      await api.post("/business-waitlist", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        business_name: form.business_name.trim(),
        location: form.location.trim(),
        website: form.website.trim() || undefined,
        source,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't submit — please try again.");
    } finally { setBusy(false); }
  };

  const inputCls = "mt-1 w-full bg-[#0D0E0F] border border-[#3E3F42] focus:border-[#57682A] outline-none px-3 py-3 text-[#E8E8EA] placeholder:text-[#6B6D70] text-sm transition-colors";
  const label = "font-mono text-xs uppercase tracking-wide text-[#8B8D90]";

  return (
    <div className="min-h-screen bg-[#050506] text-[#DAD6CE]">
      <Navbar />
      <div className="max-w-[560px] mx-auto px-6 pt-16 pb-24" data-testid="business-waitlist-page">
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#849064]">
          <Building2 className="w-4 h-4" /> OpticSwap · Business &amp; Dealer
        </div>
        <h1 className="mt-3 font-heading font-bold uppercase tracking-tight leading-[1.05] text-4xl sm:text-5xl text-[#DAD6CE]">
          Business &amp; Dealer Waitlist
        </h1>

        {sent ? (
          <div className="mt-8 border border-[#2E2F31] bg-[#111213] p-8 text-center" data-testid="business-waitlist-success">
            <CheckCircle2 className="w-11 h-11 text-olive-bright mx-auto" />
            <p className="mt-4 font-heading text-2xl uppercase tracking-tight text-[#E8E8EA]">You're on the list</p>
            <p className="mt-2 text-sm text-[#8B8D90]">We'll reach out as dealer and FFL/SOT accounts open up. A confirmation is on its way to your inbox.</p>
          </div>
        ) : (
          <>
            <p className="mt-5 text-[15px] leading-relaxed text-[#8E9195]">
              OpticSwap for dealers and FFL/SOTs is on the way. Leave your info and we'll reach out as accounts open up.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="business-waitlist-form">
              <div>
                <label htmlFor="bw-name" className={label}>Name</label>
                <input id="bw-name" data-testid="business-waitlist-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Smith" />
              </div>
              <div>
                <label htmlFor="bw-email" className={label}>Email</label>
                <input id="bw-email" type="email" data-testid="business-waitlist-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@shop.com" autoCapitalize="none" spellCheck={false} />
              </div>
              <div>
                <label htmlFor="bw-business" className={label}>Business name</label>
                <input id="bw-business" data-testid="business-waitlist-business" value={form.business_name} onChange={set("business_name")} className={inputCls} placeholder="Smith Optics &amp; Supply" />
              </div>
              <div>
                <label htmlFor="bw-location" className={label}>Location (city / state)</label>
                <input id="bw-location" data-testid="business-waitlist-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="Boise, ID" />
              </div>
              <div>
                <label htmlFor="bw-website" className={label}>Website <span className="normal-case text-[#6B6D70]">(optional)</span></label>
                <input id="bw-website" data-testid="business-waitlist-website" value={form.website} onChange={set("website")} className={inputCls} placeholder="smithoptics.com" autoCapitalize="none" spellCheck={false} />
              </div>
              <button type="submit" data-testid="business-waitlist-submit" disabled={!canSubmit}
                className="w-full inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading font-bold uppercase text-base py-3.5 transition-colors">
                {busy ? "Submitting..." : <>Join the Waitlist <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
